import { NAV_SEARCH_ITEMS } from '../../../hooks/universalSearchNav.js'

/**
 * App shell anchors: the sidebar, topbar and account menu, present on every
 * signed-in page (`route: '*'`).
 *
 * The `nav.*` page links are seeded from the command palette's navigation
 * items so the two lists can't drift. Contextual destinations are left out:
 * Categories, partitions, and templates appear only within their workspace
 * area, so they cannot advertise an anchor present on every route.
 */

const NOT_IN_SIDEBAR = new Set(['/categories', '/partitions', '/report-templates'])

/** Sidebar wording where it differs from the command palette's. */
const SIDEBAR_LABELS = { '/data-entry': 'Data entry' }

const NAV_INTENTS = {
  '/dashboards': 'Go to your dashboards (home)',
  '/data-entry': 'Enter numbers by hand with data entry forms',
  '/metrics': 'See and manage every metric',
  '/reports': 'See, create and schedule reports',
}

const seededNavAnchors = NAV_SEARCH_ITEMS
  .filter((item) => !NOT_IN_SIDEBAR.has(item.path))
  .map((item) => ({
    id: `nav.${item.id.replace(/^nav-/, '').replace(/-/g, '_')}`,
    label: SIDEBAR_LABELS[item.path] || item.name,
    intent: NAV_INTENTS[item.path] || `Go to ${item.name}`,
    route: '*',
    minRole: 'viewer',
    seededFrom: 'universalSearch',
  }))

export const SHELL_ANCHORS = [
  ...seededNavAnchors,
  { id: 'nav.menu', label: 'More', intent: 'Open the phone navigation drawer to find favorites, integrations and settings', route: '*', minRole: 'viewer' },
  { id: 'nav.integrations', label: 'Integrations', intent: 'Connect data sources and review integration setup', route: '*', minRole: 'admin' },
  { id: 'nav.settings', label: 'Settings', intent: 'Open organization settings', route: '*', minRole: 'admin' },
  { id: 'nav.ask_graphit', label: 'Ask Graphit', intent: 'Open the AI assistant from the sidebar', route: '*', minRole: 'viewer' },
  { id: 'nav.search', label: 'Search', intent: 'Search dashboards, metrics, reports and settings (Cmd+K)', route: '*', minRole: 'viewer' },
  { id: 'nav.notifications', label: 'Notifications', intent: 'See sync results, shares and other notifications', route: '*', minRole: 'viewer' },
  { id: 'nav.tenant_switcher', label: 'Switch organization', intent: 'Switch to another organization or church you belong to', route: '*', minRole: 'viewer' },
  { id: 'impersonation.exit', label: 'Exit', intent: 'Stop impersonating this user and return to your own session (shown only while impersonating)', route: '*', minRole: 'viewer' },
  { id: 'nav.account_menu', label: 'Account menu', intent: 'Profile, appearance, feedback and log out', route: '*', minRole: 'viewer' },
  { id: 'account.profile', label: 'Profile', intent: 'Change your name, password and two-factor sign-in', route: '*', minRole: 'viewer', parent: 'nav.account_menu' },
  { id: 'account.appearance', label: 'Appearance', intent: 'Switch between light, dark and system theme', route: '*', minRole: 'viewer', parent: 'nav.account_menu' },
  { id: 'account.logout', label: 'Logout', intent: 'Sign out of Graphit', route: '*', minRole: 'viewer', parent: 'nav.account_menu' },
  { id: 'help.feedback', label: 'Provide feedback', intent: 'Report a problem or send feedback to the Graphit team', route: '*', minRole: 'viewer', parent: 'nav.account_menu' },
  { id: 'integrations.setup.finish_banner', label: 'Finish setup', intent: 'Banner link into the guided Rock setup while it isn’t finished', route: '*', minRole: 'admin' },
  { id: 'nav.finish_rock_setup', label: 'Finish Rock setup', intent: 'Sidebar item back into the guided Rock setup while it isn’t finished', route: '*', minRole: 'admin' },
  { id: 'help.getting_started', label: 'Getting started', intent: 'Where your numbers live, then the steps to set it up', route: '/dashboards', minRole: 'admin' },
  { id: 'getting_started.enter_numbers', label: 'Enter this week’s numbers', intent: 'Open the weekly form made from your standard measurements (made the first time you open it)', route: '/getting-started', minRole: 'admin' },
]

/** The assistant panel itself. */
export const ASSISTANT_ANCHORS = [
  { id: 'assistant.open', label: 'Ask Graphit', intent: 'Open or close the assistant panel from the top bar', route: '*', minRole: 'viewer' },
  { id: 'assistant.composer', label: 'Message box', intent: 'Type a question for the assistant', route: '*', minRole: 'viewer', parent: 'assistant.open' },
  { id: 'assistant.persona', label: 'Assistant persona', intent: 'Change the assistant persona (tone and focus)', route: '*', minRole: 'viewer', parent: 'assistant.open' },
  { id: 'assistant.history', label: 'Conversation history', intent: 'Find an earlier conversation', route: '*', minRole: 'viewer', parent: 'assistant.open' },
  { id: 'assistant.new_chat', label: 'New chat', intent: 'Start a new conversation', route: '*', minRole: 'viewer', parent: 'assistant.open' },
  { id: 'assistant.suggestions', label: 'Suggested questions', intent: 'Starter questions for this page', route: '*', minRole: 'viewer', parent: 'assistant.open' },
]
